/**
 * CAR detail for one month.
 *
 * Complaint text and corrective actions collapse to a single summary line and
 * expand in place. The full text already arrived with the row, so expanding
 * costs no request.
 *
 * Transplanted from the reference implementation. Two changes: rows arrive keyed
 * through `shared/rows.ts` because the payload is positional (§8.6), and the
 * ternary-for-effect in `toggle` is an `if` — `strictTypeChecked` rejects an
 * expression statement whose value is discarded.
 */

import { Fragment, useMemo, useState } from 'react'

import { displayText, formatDate, statusRole, summarize } from '../shared/format'
import type { Keyed } from '../shared/rows'
import { count, text } from '../shared/rows'

type SortKey = 'car_number' | 'request_date' | 'supplier_name' | 'status' | 'location'

const STATUS_COLORS: Record<string, string> = {
  good: 'var(--status-good)',
  warning: 'var(--status-warning)',
  serious: 'var(--status-serious)',
  critical: 'var(--status-critical)',
  neutral: 'var(--text-muted)',
}

function Chevron() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
      <path
        d="M4 2.5 8 6l-4 3.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function StatusChip({ status }: { status: string | null }) {
  if (status === null) return null
  return (
    <span className="status-chip" title={status}>
      <span
        className="status-dot"
        style={{ background: STATUS_COLORS[statusRole(status)] }}
        aria-hidden="true"
      />
      {status}
    </span>
  )
}

function Field({ label, value }: { label: string; value: string | null }) {
  const body = displayText(value)
  return (
    <div className="expanded-field">
      <h4>{label}</h4>
      <p className={body === '' ? 'is-empty' : undefined}>{body === '' ? 'Not recorded' : body}</p>
    </div>
  )
}

export function DetailTable({ rows }: { rows: readonly Keyed[] }) {
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [sort, setSort] = useState<{ key: SortKey; asc: boolean }>({
    key: 'request_date',
    asc: false,
  })

  const sorted = useMemo(() => {
    const copy = [...rows]
    copy.sort((a, b) => {
      const av = a[sort.key] ?? ''
      const bv = b[sort.key] ?? ''
      const cmp =
        sort.key === 'request_date'
          ? String(av).localeCompare(String(bv))
          : String(av).localeCompare(String(bv), undefined, { numeric: true })
      return sort.asc ? cmp : -cmp
    })
    return copy
  }, [rows, sort])

  if (rows.length === 0) {
    return <div className="state">No CARs in this month for the current filters.</div>
  }

  const toggle = (id: number) => {
    const next = new Set(expanded)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    setExpanded(next)
  }

  const allOpen = expanded.size === rows.length

  const header = (key: SortKey, label: string) => (
    <th
      className="sortable"
      onClick={() => {
        setSort({ key, asc: sort.key === key ? !sort.asc : true })
      }}
      aria-sort={sort.key === key ? (sort.asc ? 'ascending' : 'descending') : 'none'}
    >
      {label}
      {sort.key === key && (sort.asc ? ' ↑' : ' ↓')}
    </th>
  )

  return (
    <>
      <div style={{ marginBottom: 8 }}>
        <button
          className="link-button"
          onClick={() => {
            setExpanded(allOpen ? new Set() : new Set(rows.map((r) => count(r.car_id))))
          }}
        >
          {allOpen ? 'Collapse all' : 'Expand all'}
        </button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="detail-table">
          {/* Fixed widths: without them the complaint column collapses to a
              sliver and every row grows tall. Complaint takes what is left.
              CAR Number and Requested are wider than the reference's 152/100 —
              the portal sets IBM Plex Sans where the original had system-ui, and
              at the original widths the two columns overlapped. */}
          <colgroup>
            <col style={{ width: 30 }} />
            <col style={{ width: 178 }} />
            <col style={{ width: 112 }} />
            <col style={{ width: 150 }} />
            <col style={{ width: 125 }} />
            <col style={{ width: 225 }} />
            <col style={{ width: 85 }} />
            <col style={{ minWidth: 300 }} />
          </colgroup>
          <thead>
            <tr>
              <th className="chevron-cell" aria-label="Expand" />
              {header('car_number', 'CAR Number')}
              {header('request_date', 'Requested')}
              {header('supplier_name', 'Vendor')}
              <th>Item</th>
              {header('status', 'Status')}
              {header('location', 'Location')}
              <th>Complaint</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => {
              const carId = count(row.car_id)
              const isOpen = expanded.has(carId)
              return (
                <Fragment key={carId}>
                  <tr
                    className={`detail-row${isOpen ? ' is-open' : ''}`}
                    onClick={() => {
                      toggle(carId)
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="chevron-cell">
                      <button
                        className={`chevron${isOpen ? ' is-open' : ''}`}
                        aria-expanded={isOpen}
                        aria-label={`${isOpen ? 'Collapse' : 'Expand'} CAR ${displayText(text(row.car_number))}`}
                        onClick={(e) => {
                          e.stopPropagation()
                          toggle(carId)
                        }}
                      >
                        <Chevron />
                      </button>
                    </td>
                    <td className="cell-mono">{displayText(text(row.car_number))}</td>
                    <td className="cell-mono">{formatDate(text(row.request_date))}</td>
                    <td className="cell-truncate" title={displayText(text(row.supplier_name))}>
                      {displayText(text(row.supplier_name))}
                    </td>
                    <td className="cell-truncate" title={displayText(text(row.item_name))}>
                      {displayText(text(row.item_name))}
                    </td>
                    <td>
                      <StatusChip status={text(row.status)} />
                    </td>
                    <td>{displayText(text(row.location))}</td>
                    <td className="cell-summary" title={displayText(text(row.complaint_text))}>
                      {summarize(text(row.complaint_text))}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td className="expanded-cell" colSpan={8}>
                        <div className="expanded-grid">
                          <Field label="Complaint text" value={text(row.complaint_text)} />
                          <Field label="Corrective actions" value={text(row.corrective_actions)} />
                        </div>
                        <div className="expanded-meta">
                          <span>Item ID: {displayText(text(row.item_id)) || '—'}</span>
                          <span>Vendor: {displayText(text(row.supplier_name)) || '—'}</span>
                          <span>Location: {displayText(text(row.location)) || '—'}</span>
                          <span>Updated: {formatDate(text(row.update_date)) || '—'}</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}
